import { calculateAverageApy } from '@/components/dashboard/useGroupedBalances/calculateAverageApy'
import type { PositionsList } from '@/queries/positions.query'
import { captureException } from '@sentry/react-router'
import { ETH_TOKENS, EURO_COINS, STABLE_COINS } from '@vaultsfyi/common/constants'
import { useMemo } from 'react'
import type { Address } from 'viem'
import { groupBalances } from './groupBalances'

export const useGroupedPositions = (
  address: Address | undefined,
  positionBalances: PositionsList | undefined,
  includeRewards: boolean
) => {
  return useMemo(() => {
    if (positionBalances === null || positionBalances === undefined) {
      return null
    }
    if (!Array.isArray(positionBalances)) {
      captureException(new Error('Invalid data for useGroupedPositions'), {
        extra: { positionBalances },
      })
      return null
    }

    const groupedPositions = groupBalances(positionBalances, getGroupName).map((data) => {
      return {
        ...data,
        groupApy: calculateAverageApy(
          data.balances.map(({ balanceUsd, apy }) => ({
            balanceUsd,
            apy: includeRewards ? apy.total : apy.base,
          }))
        ),
      }
    })

    return groupedPositions
  }, [positionBalances, address, includeRewards])
}

function getGroupName({ asset }: { asset: { symbol: string } }) {
  if (STABLE_COINS.includes(asset.symbol)) {
    return 'USD'
  }
  if (ETH_TOKENS.includes(asset.symbol)) {
    return 'ETH'
  }
  if (EURO_COINS.includes(asset.symbol)) {
    return 'EUR'
  }
  return 'OTHER'
}

export type LpTokensBalanceGroup = NonNullable<ReturnType<typeof useGroupedPositions>>
